/**
 * DAA (Decisions, Actions, Approaches) Dashboard Page
 *
 * Visual dashboard showing the Decision Audit Architecture for Kenbri Group.
 * Tracks strategic decisions, failed initiatives, and learnings across
 * the portfolio companies: Panago Pizza, Phantom Screens, Apex Food Source.
 */
import { Card, Text, Group, Stack, Badge, SimpleGrid, Timeline, ThemeIcon } from '@mantine/core';
import {
  IconBrain,
  IconCheck,
  IconX,
  IconBulb,
  IconGitBranch,
  IconClock,
  IconDatabase,
  IconArrowRight,
  IconChartBar,
  IconBuildingStore,
  IconTruck,
} from '@tabler/icons-react';
import TopNav from '../components/TopNav';

const DAA_STATS = {
  totalDecisions: 847,
  decisionsThisQuarter: 124,
  failedInitiatives: 23,
  learningsLogged: 312,
  averageConfidence: 0.84,
  portfolioReviews: 156,
};

const RECENT_DECISIONS = [
  {
    id: 'dec-2024-001',
    title: 'Panago Digital Ordering Platform Upgrade',
    context: 'Franchise Operations - Customer Experience Initiative',
    decision: 'Invest $2.4M in new mobile app and online ordering system with AI-powered recommendations',
    rationale: 'Digital orders grew 34% YoY; competitor analysis shows 18% higher ticket size on digital channels',
    confidence: 0.91,
    time: '2 days ago',
    status: 'approved',
    opco: 'Panago Pizza',
    alternatives: ['Third-party delivery partnership only', 'Incremental updates to existing system'],
  },
  {
    id: 'dec-2024-002',
    title: 'Phantom Screens US Southwest Expansion',
    context: 'Dealer Network Growth - Geographic Expansion',
    decision: 'Authorize 15 new dealer agreements in Arizona and Nevada markets',
    rationale: 'Housing starts up 12% in target markets; climate conditions ideal for retractable screens',
    confidence: 0.87,
    time: '1 week ago',
    status: 'approved',
    opco: 'Phantom Screens',
    alternatives: ['Focus on existing Pacific Northwest market', 'Direct-to-consumer pilot program'],
  },
  {
    id: 'dec-2024-003',
    title: 'Apex Food Source Organic Certification Line',
    context: 'Manufacturing Capacity - Product Line Expansion',
    decision: 'Convert Line 3 to dedicated organic production with $1.8M equipment investment',
    rationale: 'Organic private label demand up 28%; margin premium of 8-12% over conventional',
    confidence: 0.83,
    time: '2 weeks ago',
    status: 'approved',
    opco: 'Apex Food Source',
    alternatives: ['Outsource organic production', 'Hybrid line with deep cleaning protocols'],
  },
  {
    id: 'dec-2024-004',
    title: 'Portfolio-Wide Energy Efficiency Initiative',
    context: 'Holdco ESG Strategy - Cost Reduction',
    decision: 'Implement LED lighting and HVAC optimization across all facilities, ROI target 3.2 years',
    rationale: 'Utility costs up 18% across portfolio; ESG requirements from institutional investors',
    confidence: 0.89,
    time: '3 weeks ago',
    status: 'approved',
    opco: 'Kenbri Group',
    alternatives: ['Facility-by-facility approach', 'Solar installation priority'],
  },
];

const FAILED_INITIATIVES = [
  {
    id: 'fail-001',
    approach: 'Panago ghost kitchen model in downtown Vancouver',
    reason: 'Delivery-only locations underperformed by 40% vs projections; brand recognition issues',
    learning: 'Panago brand strength tied to visible storefronts; delivery customers prefer established locations',
    project: 'Panago Pizza',
    date: 'Q3 2024',
    investmentLost: '$320K',
  },
  {
    id: 'fail-002',
    approach: 'Phantom Screens direct-to-consumer e-commerce pilot',
    reason: 'Installation complexity requires dealer expertise; return rate 3x higher than dealer channel',
    learning: 'Product category requires professional measurement and installation; strengthen dealer model',
    project: 'Phantom Screens',
    date: 'Q2 2024',
    investmentLost: '$185K',
  },
  {
    id: 'fail-003',
    approach: 'Apex automated ingredient sourcing platform',
    reason: 'Quality variance from algorithmic supplier selection; client complaints increased 15%',
    learning: 'Food manufacturing requires relationship-based sourcing; quality control cannot be fully automated',
    project: 'Apex Food Source',
    date: 'Q1 2024',
    investmentLost: '$420K',
  },
];

const STRATEGIC_PILLARS = [
  { type: 'Investment', description: 'Capital allocation decisions', count: 234 },
  { type: 'Operations', description: 'Process and efficiency improvements', count: 312 },
  { type: 'Growth', description: 'Market expansion initiatives', count: 156 },
  { type: 'Risk', description: 'Mitigation and compliance', count: 89 },
  { type: 'ESG', description: 'Sustainability and governance', count: 56 },
];

const OPCO_DECISION_SUMMARY = [
  { name: 'Panago Pizza', decisions: 287, confidence: 0.86, color: '#dc2626' },
  { name: 'Phantom Screens', decisions: 234, confidence: 0.84, color: '#2563eb' },
  { name: 'Apex Food Source', decisions: 198, confidence: 0.82, color: '#16a34a' },
  { name: 'Holdco / Corporate', decisions: 128, confidence: 0.88, color: '#6366f1' },
];

export default function DAAPage() {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        background: '#f8f9fa',
      }}
    >
      <TopNav />

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between">
            <Group gap="sm">
              <div
                style={{
                  width: 48,
                  height: 48,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconBrain size={28} color="white" />
              </div>
              <div>
                <Text size="xl" fw={700}>
                  Decision Audit Architecture
                </Text>
                <Text size="sm" c="dimmed">
                  Strategic decisions, initiatives, and learnings across the Rooke portfolio
                </Text>
              </div>
            </Group>
            <Badge color="grape" size="lg" variant="light">
              AI-Powered Insights
            </Badge>
          </Group>

          {/* Stats */}
          <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconBrain size={18} color="#6366f1" />
                <Text size="xs" c="dimmed">
                  Total Decisions
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {DAA_STATS.totalDecisions.toLocaleString()}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconClock size={18} color="#22c55e" />
                <Text size="xs" c="dimmed">
                  This Quarter
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {DAA_STATS.decisionsThisQuarter}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconX size={18} color="#ef4444" />
                <Text size="xs" c="dimmed">
                  Failed Initiatives
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {DAA_STATS.failedInitiatives}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconBulb size={18} color="#f59e0b" />
                <Text size="xs" c="dimmed">
                  Learnings
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {DAA_STATS.learningsLogged}
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconCheck size={18} color="#06b6d4" />
                <Text size="xs" c="dimmed">
                  Avg Confidence
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {(DAA_STATS.averageConfidence * 100).toFixed(0)}%
              </Text>
            </Card>
            <Card withBorder padding="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconChartBar size={18} color="#ec4899" />
                <Text size="xs" c="dimmed">
                  Portfolio Reviews
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {DAA_STATS.portfolioReviews}
              </Text>
            </Card>
          </SimpleGrid>

          {/* OpCo Decision Summary */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md">Decisions by Operating Company</Text>
            <SimpleGrid cols={{ base: 2, md: 4 }}>
              {OPCO_DECISION_SUMMARY.map((opco) => (
                <div
                  key={opco.name}
                  style={{
                    padding: 16,
                    background: '#f1f5f9',
                    borderRadius: 8,
                    borderLeft: `4px solid ${opco.color}`,
                  }}
                >
                  <Text size="sm" fw={600} mb={4}>{opco.name}</Text>
                  <Group justify="space-between">
                    <div>
                      <Text size="xl" fw={700}>{opco.decisions}</Text>
                      <Text size="xs" c="dimmed">decisions</Text>
                    </div>
                    <Badge color={opco.confidence >= 0.85 ? 'green' : 'yellow'} variant="light">
                      {(opco.confidence * 100).toFixed(0)}% conf
                    </Badge>
                  </Group>
                </div>
              ))}
            </SimpleGrid>
          </Card>

          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Recent Decisions */}
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="md" fw={600}>
                  Recent Strategic Decisions
                </Text>
                <IconGitBranch size={20} color="#6b7280" />
              </Group>
              <Stack gap="md">
                {RECENT_DECISIONS.map((decision) => (
                  <div
                    key={decision.id}
                    style={{
                      padding: 12,
                      background: '#f8fafc',
                      borderRadius: 8,
                      borderLeft: '3px solid #6366f1',
                    }}
                  >
                    <Group justify="space-between" mb={4}>
                      <Text size="sm" fw={600}>
                        {decision.title}
                      </Text>
                      <Badge size="sm" color="green" variant="light">
                        {(decision.confidence * 100).toFixed(0)}%
                      </Badge>
                    </Group>
                    <Group gap="xs" mb="xs">
                      <Badge size="xs" variant="outline" color={
                        decision.opco === 'Panago Pizza' ? 'red' :
                        decision.opco === 'Phantom Screens' ? 'blue' :
                        decision.opco === 'Apex Food Source' ? 'green' : 'violet'
                      }>
                        {decision.opco}
                      </Badge>
                      <Text size="xs" c="dimmed">{decision.context}</Text>
                    </Group>
                    <Text size="sm" c="dark" mb="xs">
                      <strong>Decision:</strong> {decision.decision}
                    </Text>
                    <Text size="xs" c="dimmed">
                      <strong>Rationale:</strong> {decision.rationale}
                    </Text>
                    <Group gap="xs" mt="xs">
                      <Text size="xs" c="dimmed">
                        Alternatives considered:
                      </Text>
                      {decision.alternatives.map((alt) => (
                        <Badge key={alt} size="xs" variant="outline" color="gray">
                          {alt}
                        </Badge>
                      ))}
                    </Group>
                    <Text size="xs" c="dimmed" ta="right" mt="xs">
                      {decision.time}
                    </Text>
                  </div>
                ))}
              </Stack>
            </Card>

            {/* Failed Initiatives */}
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="md" fw={600}>
                  Failed Initiatives & Learnings
                </Text>
                <IconBulb size={20} color="#f59e0b" />
              </Group>
              <Timeline active={2} bulletSize={24} lineWidth={2}>
                {FAILED_INITIATIVES.map((item) => (
                  <Timeline.Item
                    key={item.id}
                    bullet={
                      <ThemeIcon size={24} radius="xl" color="red" variant="filled">
                        <IconX size={14} />
                      </ThemeIcon>
                    }
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {item.approach}
                        </Text>
                        <Badge size="xs" color="red" variant="light">{item.investmentLost}</Badge>
                      </Group>
                    }
                  >
                    <Badge size="xs" variant="outline" color="gray" mb={4}>
                      {item.project}
                    </Badge>
                    <Text size="xs" c="red" mb={4}>
                      {item.reason}
                    </Text>
                    <Group gap="xs" mb={4}>
                      <IconArrowRight size={12} color="#22c55e" />
                      <Text size="xs" c="green">
                        <strong>Learning:</strong> {item.learning}
                      </Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {item.date}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </SimpleGrid>

          {/* Strategic Pillars */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md">
              Decision Categories
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }}>
              {STRATEGIC_PILLARS.map((pillar) => (
                <div
                  key={pillar.type}
                  style={{
                    padding: 16,
                    background: '#f1f5f9',
                    borderRadius: 8,
                    textAlign: 'center',
                  }}
                >
                  <Text size="xl" fw={700}>
                    {pillar.count.toLocaleString()}
                  </Text>
                  <Text size="sm" fw={600} c="#6366f1" mb={4}>
                    {pillar.type}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {pillar.description}
                  </Text>
                </div>
              ))}
            </SimpleGrid>
          </Card>

          {/* Decision Flow Visualization */}
          <Card withBorder padding="lg" radius="md">
            <Text size="md" fw={600} mb="md">
              Strategic Decision Process
            </Text>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 24,
                padding: '24px 0',
                flexWrap: 'wrap',
              }}
            >
              {[
                { label: 'Data Analysis', icon: IconDatabase, color: '#3b82f6' },
                { label: 'AI Review', icon: IconBrain, color: '#8b5cf6' },
                { label: 'Decision', icon: IconGitBranch, color: '#6366f1' },
                { label: 'Execution', icon: IconCheck, color: '#22c55e' },
                { label: 'Learning', icon: IconBulb, color: '#f59e0b' },
              ].map((step, idx) => (
                <Group key={step.label} gap="md">
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        background: step.color,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 8px',
                      }}
                    >
                      <step.icon size={28} color="white" />
                    </div>
                    <Text size="sm" fw={500}>
                      {step.label}
                    </Text>
                  </div>
                  {idx < 4 && (
                    <IconArrowRight size={24} color="#cbd5e1" style={{ marginTop: -20 }} />
                  )}
                </Group>
              ))}
            </div>
          </Card>

          {/* Recent Portfolio Insights */}
          <Card withBorder padding="lg" radius="md">
            <Group justify="space-between" mb="md">
              <Text size="md" fw={600}>AI-Generated Portfolio Insights</Text>
              <Badge color="grape" variant="light">Updated daily</Badge>
            </Group>
            <SimpleGrid cols={{ base: 1, md: 3 }}>
              <div style={{ padding: 16, background: '#fef2f2', borderRadius: 8, borderLeft: '4px solid #dc2626' }}>
                <Group gap="xs" mb="xs">
                  <IconBuildingStore size={18} color="#dc2626" />
                  <Text size="sm" fw={600}>Panago Pizza</Text>
                </Group>
                <Text size="sm">
                  Same-store sales momentum continues; recommend accelerating digital investment.
                  BC market saturation suggests Ontario expansion priority for Q2 2025.
                </Text>
              </div>
              <div style={{ padding: 16, background: '#eff6ff', borderRadius: 8, borderLeft: '4px solid #2563eb' }}>
                <Group gap="xs" mb="xs">
                  <IconTruck size={18} color="#2563eb" />
                  <Text size="sm" fw={600}>Phantom Screens</Text>
                </Group>
                <Text size="sm">
                  US Mountain region showing 15% above-target growth. Consider increasing dealer
                  incentives for winter slow season to maintain momentum through Q1.
                </Text>
              </div>
              <div style={{ padding: 16, background: '#f0fdf4', borderRadius: 8, borderLeft: '4px solid #16a34a' }}>
                <Group gap="xs" mb="xs">
                  <IconDatabase size={18} color="#16a34a" />
                  <Text size="sm" fw={600}>Apex Food Source</Text>
                </Group>
                <Text size="sm">
                  Specialty Food Brands segment outperforming by 10.2% YoY. Organic line conversion
                  on track; monitor QSR segment for potential client consolidation risks.
                </Text>
              </div>
            </SimpleGrid>
          </Card>
        </Stack>
      </div>
    </div>
  );
}
